import React, { useState, useEffect } from 'react';
import { UserPlus, Download, Search, SlidersHorizontal, FileUp, X } from 'lucide-react';
import { employeesAPI } from '../../api/employees';
import { departmentsAPI } from '../../api/services';
import api from '../../api/config';
import EmployeeFormModal from './components/EmployeeFormModal';
import EmployeeTable from './components/EmployeeTable';
import EmployeeFileModal from './components/EmployeeFileModal';
import EmployeeBulkImportModule from './BulkImport/EmployeeBulkImportModule';

const exportEmployeesComplete = (employees) => {
  const headers = [
    'ID','Nombre','Apellido','Cédula','Fecha Nacimiento','Edad','Sexo',
    'Nacionalidad','País','Tipo Sangre','Profesión','Teléfono','Celular',
    'Email','Dirección','NSS','Nivel Educativo','Contacto Emergencia',
    'Departamento','Cargo','Fecha Ingreso','Salario','Tipo Contrato',
    'Tipo Nómina','Estado Empleado','Turno','Tipo Empleado',
    'Descuento AFP','Descuento SFS','Descuento ISR','Banco','Cuenta Banco','Estado','Fecha Registro'
  ];
  const csvContent = [
    headers.join(','),
    ...employees.map(emp => [
      emp.employeeId || emp.employee_id || '',
      emp.nombre || '', emp.apellido || '', emp.cedula || '',
      emp.fechaNacimiento || '', emp.edad || '', emp.sexo || '',
      emp.nacionalidad || '', emp.pais || '', emp.tipoSangre || '',
      emp.profesion || '', emp.telefono || '', emp.celular || '',
      emp.email || '', `"${(emp.direccion || '').replace(/"/g, '""')}"`,
      emp.nss || '', emp.nivelEducativo || '', emp.contactoEmergencia || '',
      emp.departamento || emp.departamento_nombre || '',
      emp.cargo || emp.cargo_nombre || '',
      emp.fechaIngreso || '', emp.salario || '', emp.tipoContrato || '',
      emp.tipoNomina || '', emp.estadoEmpleado || '', emp.turno || '',
      emp.tipoEmpleado || '',
      emp.descuentoAfp ? 'Sí' : 'No', emp.descuentoSfs ? 'Sí' : 'No',
      emp.descuentoIsr ? 'Sí' : 'No',
      emp.nombreBanco || '', emp.cuentaBanco || '',
      emp.estado || '', emp.fechaRegistro || ''
    ].join(','))
  ].join('\n');
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.setAttribute('href', URL.createObjectURL(blob));
  link.setAttribute('download', `empleados_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default function EmployeesModule({ currentUser }) {
  const isEmployee = currentUser?.role === 'employee';
  const [employees, setEmployees]           = useState([]);
  const [departments, setDepartments]       = useState([]);
  const [orgUnits, setOrgUnits]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [showFormModal, setShowFormModal]   = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [searchTerm, setSearchTerm]         = useState('');
  const [filterStatus, setFilterStatus]     = useState('todos');
  const [filterUnit, setFilterUnit]         = useState('todos');
  const [filterDepartment, setFilterDepartment] = useState('todos');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showFilters, setShowFilters]       = useState(false);

  useEffect(() => { loadInitialData(); }, [filterStatus]); // eslint-disable-line

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [employeesRes, departmentsRes, orgUnitsRes] = await Promise.all([
        employeesAPI.getAll({ estado: filterStatus === 'todos' ? undefined : filterStatus }),
        departmentsAPI.getAll(),
        api.get('/organizational-units').catch(() => ({ data: [] }))
      ]);
      const emps = employeesRes.employees || [];
      setEmployees(emps);
      setDepartments(departmentsRes || []);
      setOrgUnits(orgUnitsRes.data || []);
      // Auto-open own profile for employee role
      if (isEmployee && emps.length === 1) {
        setViewingEmployee(emps[0]);
      }
    } catch (error) {
      console.error('❌ Error loading data:', error);
      if (error.code === 'ERR_NETWORK') {
        alert('No se puede conectar al servidor.');
      } else if (error.response?.status === 401) {
        alert('Sesión expirada. Por favor inicia sesión nuevamente.');
        window.location.href = '/';
      } else {
        alert('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  };

  // Nombres de departamentos en la unidad seleccionada
  const deptNamesInUnit = filterUnit === 'todos'
    ? null
    : departments.filter(d => d.organizational_unit_id === filterUnit).map(d => d.nombre);

  // Departamentos disponibles en el dropdown (filtrados por unidad si aplica)
  const availableDepts = filterUnit === 'todos'
    ? departments
    : departments.filter(d => d.organizational_unit_id === filterUnit);

  const filteredEmployees = employees.filter(emp => {
    const matchSearch = searchTerm === '' || (
      emp.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.apellido?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.cedula?.includes(searchTerm) ||
      emp.employee_id?.includes(searchTerm) ||
      emp.telefono?.includes(searchTerm) ||
      emp.celular?.includes(searchTerm)
    );
    const matchUnit       = !deptNamesInUnit || deptNamesInUnit.includes(emp.departamento);
    const matchDepartment = filterDepartment === 'todos' || emp.departamento === filterDepartment;
    return matchSearch && matchUnit && matchDepartment;
  });

  const handleNewEmployee  = () => { setEditingEmployee(null); setShowFormModal(true); };
  const handleEdit         = (emp) => { setEditingEmployee(emp); setShowFormModal(true); };
  const handleViewDetails  = (emp) => setViewingEmployee(emp);
  const handleCloseDetails = () => setViewingEmployee(null);
  const handleCancel       = () => { setShowFormModal(false); setEditingEmployee(null); };
  const handleEditFromDetail = (emp) => { setEditingEmployee(emp); setShowFormModal(true); };

  const handleSave = async (employeeData) => {
    try {
      if (editingEmployee) {
        await employeesAPI.update(editingEmployee.id, employeeData);
        alert('Empleado actualizado exitosamente');
      } else {
        await employeesAPI.create(employeeData);
        alert('Empleado creado exitosamente');
      }
      setShowFormModal(false);
      setEditingEmployee(null);
      setViewingEmployee(null); // volver a la lista tras guardar
      loadInitialData();
    } catch (error) {
      if (error.response?.status === 409) {
        alert('Error: La cédula ya existe en el sistema');
      } else {
        alert('Error al guardar empleado: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de que desea desactivar este empleado?')) return;
    try {
      await employeesAPI.delete(id);
      alert('Empleado desactivado exitosamente');
      loadInitialData();
    } catch {
      alert('Error al eliminar empleado');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Cargando empleados...</p>
        </div>
      </div>
    );
  }

  // ── Form view (prioridad sobre el detail) ────────────────────
  if (showFormModal) {
    return (
      <EmployeeFormModal
        employee={editingEmployee}
        departments={departments}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    );
  }

  // ── Detail view (full page) ──────────────────────────────────
  if (viewingEmployee) {
    return (
      <EmployeeFileModal
        employee={viewingEmployee}
        onClose={handleCloseDetails}
        onEdit={isEmployee ? null : handleEditFromDetail}
        readOnly={isEmployee}
      />
    );
  }

  const activos   = employees.filter(e => e.estado === 'activo').length;
  const inactivos = employees.filter(e => e.estado === 'inactivo').length;

  return (
    <div className="space-y-4">

      {/* ── Page Header ──────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
          {filteredEmployees.length} de {employees.length}
        </span>

        {!isEmployee && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => exportEmployeesComplete(filteredEmployees)}
              className="p-2 text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              title="Exportar CSV"
            >
              <Download size={16} />
            </button>
            <button
              onClick={() => setShowBulkImport(true)}
              className="p-2 text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              title="Importar empleados"
            >
              <FileUp size={16} />
            </button>
            <button
              onClick={handleNewEmployee}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              <UserPlus size={15} />
              Agregar empleado
            </button>
          </div>
        )}
      </div>

      {/* ── Search + Filter bar ──────────────────────────────────── */}
      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar empleado..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filters toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors ${
            showFilters || filterDepartment !== 'todos' || filterStatus !== 'todos' || filterUnit !== 'todos'
              ? 'border-blue-400 text-blue-600 bg-blue-50'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50'
          }`}
        >
          <SlidersHorizontal size={15} />
          Filtros
          {(filterDepartment !== 'todos' || filterStatus !== 'todos' || filterUnit !== 'todos') && (
            <span className="w-2 h-2 rounded-full bg-blue-500" />
          )}
        </button>

        {/* Stats badges */}
        <div className="hidden sm:flex items-center gap-2 ml-auto">
          <span className="text-xs text-gray-400">
            <span className="font-semibold text-green-600">{activos}</span> activos
          </span>
          <span className="text-gray-200">|</span>
          <span className="text-xs text-gray-400">
            <span className="font-semibold text-red-500">{inactivos}</span> inactivos
          </span>
          <span className="text-gray-200">|</span>
          <span className="text-xs text-gray-400">
            <span className="font-semibold text-blue-600">{departments.length}</span> depts.
          </span>
          {orgUnits.length > 0 && (
            <>
              <span className="text-gray-200">|</span>
              <span className="text-xs text-gray-400">
                <span className="font-semibold text-indigo-600">{orgUnits.length}</span> unidades
              </span>
            </>
          )}
        </div>
      </div>

      {/* ── Filter dropdowns (expandible) ───────────────────────── */}
      {showFilters && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg flex-wrap">
          {/* Unidad organizativa */}
          {orgUnits.length > 0 && (
            <select
              value={filterUnit}
              onChange={(e) => { setFilterUnit(e.target.value); setFilterDepartment('todos'); }}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
            >
              <option value="todos">Todas las unidades</option>
              {orgUnits.map(u => (
                <option key={u.id} value={u.id}>{u.nombre}</option>
              ))}
            </select>
          )}
          {/* Departamento (cascaded por unidad) */}
          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="todos">Todos los departamentos</option>
            {availableDepts.map(dept => (
              <option key={dept.id} value={dept.nombre}>{dept.nombre}</option>
            ))}
          </select>
          {/* Estado */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="todos">Todos los estados</option>
            <option value="activo">Activos</option>
            <option value="inactivo">Inactivos</option>
          </select>
          {(filterDepartment !== 'todos' || filterStatus !== 'todos' || filterUnit !== 'todos') && (
            <button
              onClick={() => { setFilterDepartment('todos'); setFilterStatus('todos'); setFilterUnit('todos'); }}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <X size={12} /> Limpiar filtros
            </button>
          )}
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────── */}
      <EmployeeTable
        employees={filteredEmployees}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onViewDetails={handleViewDetails}
      />

      {showBulkImport && (
        <EmployeeBulkImportModule
          onClose={() => setShowBulkImport(false)}
          onSuccess={loadInitialData}
        />
      )}
    </div>
  );
}
